import "@/App.css";
import { useEffect, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { restorePendingPurchases } from "@/lib/billing";

if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

function ScrollManager() {
  const location = useLocation();
  const key = location.pathname + location.search;

  // Ogni nuova pagina/sezione si apre sempre dall'alto: nessuna posizione ereditata,
  // anche tornando indietro/avanti (POP). Unica eccezione: ancore #esplicite.
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [key, location.hash]);
  return null;
}

function BillingRestore() {
  const { user, loading } = useAuth();
  useEffect(() => {
    if (!loading && user) restorePendingPurchases();
  }, [loading, user]);
  return null;
}

function AndroidBackButton() {  const navigate = useNavigate();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handle;
    CapApp.addListener("backButton", () => {
      const idx = window.history.state?.idx ?? 0;
      if (idx > 0) {
        navigate(-1);
      } else {
        CapApp.minimizeApp();
      }
    }).then((h) => { handle = h; });
    return () => { handle?.remove(); };
  }, [navigate]);
  return null;
}
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import ChiSono from "@/pages/ChiSono";
import ComeFunziona from "@/pages/ComeFunziona";
import Carte from "@/pages/Carte";
import SmazzoStesa from "@/pages/SmazzoStesa";
import Geomanzia from "@/pages/Geomanzia";
import Piacentine from "@/pages/Piacentine";
import Baraja from "@/pages/Baraja";
import Oroscopo from "@/pages/Oroscopo";
import Tarocchi from "@/pages/Tarocchi";
import Napoletane from "@/pages/Napoletane";
import Oracolo from "@/pages/Oracolo";
import Stesa26 from "@/pages/Stesa26";
import Notifiche from "@/pages/Notifiche";
import Auth from "@/pages/Auth";
import Privacy from "@/pages/Privacy";
import EliminaAccount from "@/pages/EliminaAccount";
import Termini from "@/pages/Termini";
import Disclaimer from "@/pages/Disclaimer";
import OltreLeCarte from "@/pages/OltreLeCarte";
import OltreLeCartePost from "@/pages/OltreLeCartePost";
import Recensioni from "@/pages/Recensioni";
import Rune from "@/pages/Rune";
import IChing from "@/pages/IChing";
import Assistente from "@/pages/Assistente";
import Piani from "@/pages/Piani";
import AreaCliente from "@/pages/AreaCliente";
import MieLetture from "@/pages/MieLetture";
import StoricoCrediti from "@/pages/StoricoCrediti";
import Segnala from "@/pages/Segnala";
import Admin from "@/pages/admin/Admin";
import Cielo from "@/pages/Cielo";
import ScopriAltro from "@/pages/ScopriAltro";
import Numerologia from "@/pages/Numerologia";
import RecuperaPassword from "@/pages/RecuperaPassword";
import ReimpostaPassword from "@/pages/ReimpostaPassword";
import PaymentResult from "@/pages/PaymentResult";
import Memoria from "@/pages/Memoria";
import ReimpostaPin from "@/pages/ReimpostaPin";
import ConfermaEmail from "@/pages/ConfermaEmail";
import VerificaEmail from "@/pages/VerificaEmail";

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0f0617]">
    <img src="/arcana-wordmark.png" alt="ARCANA" className="h-6 w-auto animate-pulse" />
  </div>
);

const Protected = ({ children, admin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to={`/accedi?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollManager />
        <AndroidBackButton />
        <AuthProvider>
          <BillingRestore />
          <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/chi-sono" element={<ChiSono />} />
              <Route path="/come-funziona" element={<ComeFunziona />} />
              <Route path="/metodi" element={<Navigate to="/scopri-altro" replace />} />
              <Route path="/carte" element={<Carte />} />
              <Route path="/lettura" element={<SmazzoStesa />} />
              <Route path="/geomanzia" element={<Geomanzia />} />
              <Route path="/piacentine" element={<Piacentine />} />
              <Route path="/baraja" element={<Baraja />} />
              <Route path="/tarocchi" element={<Tarocchi />} />
              <Route path="/napoletane" element={<Napoletane />} />
              <Route path="/oracolo" element={<Oracolo />} />
              <Route path="/stesa26" element={<Stesa26 />} />
              <Route path="/smazzo-e-stesa" element={<SmazzoStesa />} />
              <Route path="/accedi" element={<Auth />} />
              <Route path="/recupera-password" element={<RecuperaPassword />} />
              <Route path="/reimposta-password" element={<ReimpostaPassword />} />
              <Route path="/payment/success" element={<PaymentResult />} />
              <Route path="/payment/cancel" element={<PaymentResult />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/elimina-account" element={<EliminaAccount />} />
              <Route path="/termini" element={<Termini />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/segnala" element={<Segnala />} />
              <Route path="/oltre-le-carte" element={<OltreLeCarte />} />
              <Route path="/recensioni" element={<Recensioni />} />
              <Route path="/oltre-le-carte/:id" element={<OltreLeCartePost />} />
              <Route path="/rune" element={<Rune />} />
          <Route path="/iching" element={<IChing />} />
              <Route path="/assistente" element={<Assistente />} />
              <Route path="/piani" element={<Piani />} />
              <Route path="/cielo" element={<Cielo />} />
              <Route path="/oroscopo" element={<Oroscopo />} />
              <Route path="/diario" element={<Navigate to="/cielo" replace />} />
              <Route path="/coppia" element={<Navigate to="/cielo" replace />} />
              <Route path="/scopri-altro" element={<ScopriAltro />} />
              <Route path="/numerologia" element={<Numerologia />} />
              <Route path="/oroscopo" element={<Navigate to="/cielo" replace />} />
              <Route path="/memoria" element={<Protected><Memoria /></Protected>} />
              <Route path="/memoria/reimposta-pin" element={<ReimpostaPin />} />
              <Route path="/conferma-email" element={<ConfermaEmail />} />
              <Route path="/verifica-email" element={<VerificaEmail />} />
              <Route path="/cliente" element={<Protected><AreaCliente /></Protected>} />
              <Route path="/letture" element={<Protected><MieLetture /></Protected>} />
              <Route path="/crediti" element={<Protected><StoricoCrediti /></Protected>} />
              <Route path="/notifiche" element={<Protected><Notifiche /></Protected>} />
              <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </Suspense>
          <Toaster theme="dark" position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
